import React, { ReactNode, useMemo } from 'react';

type ClientName = 'web' | 'admin' | 'blog' | 'docs' | 'snaps';
type ClientUrls = Record<ClientName, string>;

type ClientContextValue = {
	client: ClientName;
	clients: ClientUrls;
	platformHref: (path?: string) => string;
	clientHref: (client: ClientName, path?: string) => string;
};

type ClientProviderProps = {
	client: ClientName;
	children: ReactNode;
};

const ClientContext = React.createContext<ClientContextValue | null>(null);

const clients: ClientUrls = {
	web: process.env.NEXT_PUBLIC_WEB_CLIENT || '',
	admin: process.env.NEXT_PUBLIC_ADMIN_CLIENT || '',
	blog: process.env.NEXT_PUBLIC_BLOG_CLIENT || '',
	docs: process.env.NEXT_PUBLIC_DOCS_CLIENT || '',
	snaps: process.env.NEXT_PUBLIC_SNAPS_CLIENT || '',
};

const isExternalHref = (href: string) => /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);

const joinHref = (root: string, path = '/') => {
	if (isExternalHref(path)) return path;
	if (!root) throw new Error('Client URL is not configured.');

	const normalizedRoot = root === '/' ? '' : root.replace(/\/+$/, '');
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	return `${normalizedRoot}${normalizedPath}` || '/';
};

const ClientProvider = ({ client, children }: ClientProviderProps) => {
	// Computed
	const missingClients = Object.entries(clients)
		.filter(([, client]) => !client)
		.map(([name]) => name);
	if (missingClients.length) throw new Error(`Client URLs are not configured: ${missingClients.join(', ')}.`);

	const value = useMemo<ClientContextValue>(
		() => ({
			client,
			clients,
			platformHref: path => joinHref(clients.web, path),
			clientHref: (targetClient, path) => joinHref(clients[targetClient], path),
		}),
		[client],
	);

	// Render
	return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
};

const useClient = () => {
	const context = React.useContext(ClientContext);
	if (!context) throw new Error('useClient must be used within a ClientProvider.');
	return context;
};

export { ClientProvider, useClient };
export type { ClientUrls, ClientContextValue, ClientName, ClientProviderProps };
